import { PUBLIC_LOG_LEVEL } from "$env/static/public";

/**
 * Logger.
 */
export enum LogLevels {
  all = 1,
  trace = 2,
  silly = 3,
  debug = 4,
  info = 5,
  http = 6,
  warn = 7,
  error = 8,
  fatal = 9,
}

type LogFn = (...data: Array<any>) => void;
interface ILogConfig {
  level: "trace" | "all" | "silly" | "debug" | "info" | "http" | "warn" | "error" | "fatal";
}

export class Logger {
  public silly!: LogFn;
  public trace!: LogFn;
  public debug!: LogFn;
  public info!: LogFn;
  public http!: LogFn;
  public warn!: LogFn;
  public error!: LogFn;
  public fatal!: LogFn;

  private level!: LogLevels;
  private levelMap = new Map<number, string>(
    Object.keys(LogLevels).map(v => [LogLevels[v as keyof typeof LogLevels], v])
  );

  private dateFormat = new Intl.DateTimeFormat("sv", {
    dateStyle: "short",
    timeStyle: "medium",
    timeZone: "UTC",
  }).format;

  constructor(config?: ILogConfig) {
    this.logLevelIdentifier(config);
  }

  public setLevel(logLevel: string) {
    const logLevels = Object.keys(LogLevels);

    if (!logLevels.includes(logLevel)) {
      throw new Error(
        "Invalid log level set in LOG_LEVEL environment variable. Accepted values are: '" +
          logLevels.join("', '") +
          "'."
      );
    }

    this.level = LogLevels[logLevel as keyof typeof LogLevels] - 1;
    this.setOutputLoggers();
  }

  private setOutputLoggers() {
    this.trace = this.loggerFactory(LogLevels.trace, console.trace);
    this.silly = this.loggerFactory(LogLevels.silly, console.log);
    this.debug = this.loggerFactory(LogLevels.debug, console.debug || console.log);
    this.info = this.loggerFactory(LogLevels.info, console.info);
    this.http = this.loggerFactory(LogLevels.http, console.log);
    this.warn = this.loggerFactory(LogLevels.warn, console.warn);
    this.error = this.loggerFactory(LogLevels.error, console.error);
    this.fatal = this.loggerFactory(LogLevels.fatal, console.error);
  }

  private loggerFactory(logLevel: LogLevels, cb: LogFn) {
    if (this.level < logLevel) {
      return (...args: any) => {
        cb(`${this.levelText(logLevel)}:`, ...args, "\x1b[0m");
      };
    }

    return (...args: any) => {};
  }

  private levelText(logLevel: LogLevels) {
    const timestamp = this.dateFormat(new Date());
    const text = `${timestamp} [${this.levelMap.get(logLevel)}]`;
    // See for more colours - https://en.wikipedia.org/wiki/ANSI_escape_code#In_C
    switch (logLevel) {
      case LogLevels.fatal:
        return `\x1b[41m${text}`;

      case LogLevels.error:
        return `\x1b[91m${text}`;

      case LogLevels.trace:
        return `\x1b[45m${text}`;

      case LogLevels.warn:
        return `\x1b[93m${text}`;

      case LogLevels.http:
        return `\x1b[44m${text}`;

      case LogLevels.info:
        return `\x1b[92m${text}`;

      case LogLevels.debug:
        return `\x1b[95m${text}`;

      case LogLevels.silly:
      default:
        return `\x1b[96m${text}`;
    }
  }

  private logLevelIdentifier(config?: ILogConfig) {
    if (config?.level) {
      return this.setLevel(config.level);
    }

    // From env file
    const level = PUBLIC_LOG_LEVEL || "fatal";
    this.setLevel(level.toLowerCase());
  }
}

// See - https://github.com/chalk/chalk#readme
export const logger = new Logger();
